import * as React from "react"
import { Drawer as Vaul } from "vaul"
import { cn } from "@/lib/utils"

const Sheet = Vaul.Root
const SheetTrigger = Vaul.Trigger
const SheetPortal = Vaul.Portal
const SheetClose = Vaul.Close

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof Vaul.Overlay>,
  React.ComponentPropsWithoutRef<typeof Vaul.Overlay>
>(({ className, ...props }, ref) => (
  <Vaul.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-50 bg-neutral-900/45 dark:bg-black/60 backdrop-blur-[2px]", className)}
    {...props}
  />
))
SheetOverlay.displayName = "SheetOverlay"

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof Vaul.Content> {
  hideHandle?: boolean
  side?: "bottom" | "left" | "right"
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof Vaul.Content>,
  SheetContentProps
>(({ className, children, hideHandle = false, side = "bottom", ...props }, ref) => {
  const sideClasses = {
    bottom:
      "fixed inset-x-0 bottom-0 mt-24 flex h-auto flex-col rounded-t-xl border-b-0 max-h-[92dvh]",
    left: "fixed inset-y-0 left-0 flex h-full w-[85vw] max-w-sm flex-col rounded-r-xl border-r-0",
    right:
      "fixed inset-y-0 right-0 flex h-full w-[85vw] max-w-sm flex-col rounded-l-xl border-l-0",
  }

  return (
    <SheetPortal>
      <SheetOverlay />
      <Vaul.Content
        ref={ref}
        className={cn(
          "z-50 border border-line bg-card text-card-foreground shadow-xl outline-none",
          sideClasses[side],
          className
        )}
        {...props}
      >
        {!hideHandle && side === "bottom" && (
          <div className="mx-auto mt-3 mb-1 h-1 w-9 shrink-0 rounded-full bg-neutral-300 dark:bg-neutral-700" />
        )}
        {children}
      </Vaul.Content>
    </SheetPortal>
  )
})
SheetContent.displayName = "SheetContent"

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-row items-center justify-between gap-3 px-5 pt-3 pb-4",
      className
    )}
    {...props}
  />
)
SheetHeader.displayName = "SheetHeader"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof Vaul.Title>,
  React.ComponentPropsWithoutRef<typeof Vaul.Title>
>(({ className, ...props }, ref) => (
  <Vaul.Title
    ref={ref}
    className={cn("font-display text-h2 leading-tight text-neutral-900 dark:text-neutral-50", className)}
    {...props}
  />
))
SheetTitle.displayName = "SheetTitle"

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof Vaul.Description>,
  React.ComponentPropsWithoutRef<typeof Vaul.Description>
>(({ className, ...props }, ref) => (
  <Vaul.Description
    ref={ref}
    className={cn("text-body-sm text-neutral-500 dark:text-neutral-400", className)}
    {...props}
  />
))
SheetDescription.displayName = "SheetDescription"

const SheetBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex-1 overflow-y-auto px-5 pb-6", className)} {...props} />
)
SheetBody.displayName = "SheetBody"

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "sticky bottom-0 border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-5 pt-3 pb-safe flex flex-col gap-2",
      className
    )}
    {...props}
  />
)
SheetFooter.displayName = "SheetFooter"

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetBody,
  SheetFooter,
}
